import { memo } from 'react'

function fmtTime(raw: string): string {
  if (!raw) return ''
  if (raw.includes('T') && raw.includes('-')) {
    try {
      const d = new Date(raw)
      if (!isNaN(d.getTime())) {
        return d.toLocaleString([], { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })
      }
    } catch {}
  }
  return raw
}


type SelectedEntity = {
  id?: string | number | null
  entityType?: string | null
  title?: string | null
  name?: string | null
  callsign?: string | null
  description?: string | null
  body?: string | null
  activity?: string | null
  origin?: string | null
  destination?: string | null
  operator?: string | null
  country?: string | null
  flag?: string | null
  place?: string | null
  time?: string | null
  severity?: string | null
  source?: string | null
  confidence?: string | null
  sourceCount?: number | null
  corroboration?: string | null
  sourceClass?: string | null
  maturity?: string | null
  freshnessLabel?: string | null
  altitude?: number | null
  speed?: number | null
  heading?: number | null
  altitudeKm?: number | null
  magnitude?: number | null
  depth?: number | null
  alertLevel?: string | null
  lat?: number | null
  lon?: number | null
}

type Props = {
  selectedItem: SelectedEntity | null
  activeTab: string
  activeFeedItem: unknown
  onClose: () => void
  flyTo: (lat: number, lon: number, altM?: number) => void
}

function WorldviewSelectedItemModal({ selectedItem, activeTab, activeFeedItem, onClose, flyTo }: Props) {
  if (activeTab !== 'worldview' || !selectedItem || activeFeedItem || selectedItem.entityType === 'camera') return null

  try {
    const safeSelectedItem = selectedItem && typeof selectedItem === 'object' ? selectedItem : null
    if (!safeSelectedItem) return null
    const selectedItemId = safeSelectedItem.id != null ? String(safeSelectedItem.id) : 'selected-item'
    const selectedEntityType = typeof safeSelectedItem.entityType === 'string' ? safeSelectedItem.entityType : 'entity'
    const entityTypeLabel = selectedEntityType.toUpperCase()
    const modalTitle = typeof safeSelectedItem.title === 'string' && safeSelectedItem.title
      ? safeSelectedItem.title
      : typeof safeSelectedItem.name === 'string' && safeSelectedItem.name
        ? safeSelectedItem.name
        : typeof safeSelectedItem.callsign === 'string' && safeSelectedItem.callsign
          ? safeSelectedItem.callsign
          : selectedItemId
    const modalSummary = [
      typeof safeSelectedItem.description === 'string' ? safeSelectedItem.description : '',
      typeof safeSelectedItem.body === 'string' ? safeSelectedItem.body : '',
      typeof safeSelectedItem.activity === 'string' ? safeSelectedItem.activity : '',
      safeSelectedItem.origin || safeSelectedItem.destination ? `${String(safeSelectedItem.origin ?? '')} → ${String(safeSelectedItem.destination ?? '')}` : '',
      safeSelectedItem.operator ? `${String(safeSelectedItem.operator)}${safeSelectedItem.country ? ` · ${String(safeSelectedItem.country)}` : ''}` : '',
      safeSelectedItem.flag ? `Flag: ${String(safeSelectedItem.flag)}${safeSelectedItem.destination ? ` · ${String(safeSelectedItem.destination)}` : ''}` : '',
      typeof safeSelectedItem.place === 'string' ? safeSelectedItem.place : '',
    ].filter(line => typeof line === 'string' && line.trim().length > 0)
    const timeValue = safeSelectedItem.time
    const severityValue = safeSelectedItem.severity
    const sourceValue = safeSelectedItem.source
    const confidenceValue = safeSelectedItem.confidence ? String(safeSelectedItem.confidence) : ''
    const sourceCountValue = typeof safeSelectedItem.sourceCount === 'number' && Number.isFinite(safeSelectedItem.sourceCount) ? safeSelectedItem.sourceCount : null
    const corroborationValue = safeSelectedItem.corroboration ? String(safeSelectedItem.corroboration) : ''
    const sourceClassValue = safeSelectedItem.sourceClass ? String(safeSelectedItem.sourceClass) : ''
    const maturityValue = safeSelectedItem.maturity ? String(safeSelectedItem.maturity) : ''
    const freshnessValue = safeSelectedItem.freshnessLabel ? String(safeSelectedItem.freshnessLabel) : ''
    const altitudeValue = typeof safeSelectedItem.altitude === 'number' && Number.isFinite(safeSelectedItem.altitude) ? safeSelectedItem.altitude : null
    const speedValue = typeof safeSelectedItem.speed === 'number' && Number.isFinite(safeSelectedItem.speed) ? safeSelectedItem.speed : null
    const headingValue = typeof safeSelectedItem.heading === 'number' && Number.isFinite(safeSelectedItem.heading) ? safeSelectedItem.heading : null
    const altitudeKmValue = typeof safeSelectedItem.altitudeKm === 'number' && Number.isFinite(safeSelectedItem.altitudeKm) ? safeSelectedItem.altitudeKm : null
    const magnitudeValue = typeof safeSelectedItem.magnitude === 'number' && Number.isFinite(safeSelectedItem.magnitude) ? safeSelectedItem.magnitude : null
    const depthValue = typeof safeSelectedItem.depth === 'number' && Number.isFinite(safeSelectedItem.depth) ? safeSelectedItem.depth : null
    const alertLevelValue = safeSelectedItem.alertLevel ? String(safeSelectedItem.alertLevel) : ''
    const latValue = typeof safeSelectedItem.lat === 'number' && Number.isFinite(safeSelectedItem.lat) ? safeSelectedItem.lat : null
    const lonValue = typeof safeSelectedItem.lon === 'number' && Number.isFinite(safeSelectedItem.lon) ? safeSelectedItem.lon : null
    const focusAltitude = selectedEntityType === 'satellite' ? 3000000 : selectedEntityType === 'flight' ? 800000 : selectedEntityType === 'maritime' ? 600000 : 1800000

    return (
      <div className="worldview-modal-backdrop" onClick={onClose}>
        <div className="worldview-entity-modal worldview-entity-modal-cinematic" onClick={e => e.stopPropagation()}>
          <div className="worldview-entity-modal-orbit worldview-entity-modal-orbit-outer" />
          <div className="worldview-entity-modal-orbit worldview-entity-modal-orbit-inner" />
          <div className="worldview-entity-modal-header">
            <div>
              <div className="eyebrow">{entityTypeLabel} · LIVE TELEMETRY</div>
              <h3>{modalTitle}</h3>
            </div>
            <button className="rdrawer-close" onClick={onClose}>×</button>
          </div>
          <div className="worldview-entity-modal-grid">
            <div className="worldview-entity-modal-main">
              {modalSummary.map((line, index) => <p key={`${selectedItemId}-summary-${index}`}>{line}</p>)}
            </div>
            <div className="worldview-entity-modal-side">
              {timeValue != null && <div className="kv"><span>Time</span><strong>{fmtTime(String(timeValue))}</strong></div>}
              {severityValue != null && <div className="kv"><span>Severity</span><strong>{String(severityValue)}</strong></div>}
              {sourceValue != null && <div className="kv"><span>Source</span><strong>{String(sourceValue)}</strong></div>}
              {maturityValue && <div className="kv"><span>Maturity</span><strong>{maturityValue}</strong></div>}
              {confidenceValue && <div className="kv"><span>Confidence</span><strong>{confidenceValue}</strong></div>}
              {corroborationValue && <div className="kv"><span>Corroboration</span><strong>{corroborationValue}</strong></div>}
              {sourceClassValue && <div className="kv"><span>Source Class</span><strong>{sourceClassValue}</strong></div>}
              {sourceCountValue != null && <div className="kv"><span>Source Count</span><strong>{sourceCountValue}</strong></div>}
              {freshnessValue && <div className="kv"><span>Freshness</span><strong>{freshnessValue}</strong></div>}
              {altitudeValue != null && <div className="kv"><span>Altitude</span><strong>{altitudeValue.toLocaleString()} ft</strong></div>}
              {speedValue != null && <div className="kv"><span>Speed</span><strong>{speedValue} kt</strong></div>}
              {headingValue != null && <div className="kv"><span>Heading</span><strong>{headingValue}°</strong></div>}
              {altitudeKmValue != null && <div className="kv"><span>Altitude</span><strong>{altitudeKmValue.toFixed(0)} km</strong></div>}
              {magnitudeValue != null && <div className="kv"><span>Magnitude</span><strong>M{magnitudeValue.toFixed(1)}</strong></div>}
              {depthValue != null && <div className="kv"><span>Depth</span><strong>{depthValue} km</strong></div>}
              {alertLevelValue && <div className="kv"><span>Alert</span><strong>{alertLevelValue.toUpperCase()}</strong></div>}
              {latValue != null && lonValue != null && <div className="kv"><span>Coordinates</span><strong>{latValue.toFixed(3)}, {lonValue.toFixed(3)}</strong></div>}
              {latValue != null && lonValue != null && (
                <button className="button worldview-modal-action" onClick={() => flyTo(latValue, lonValue, focusAltitude)}>
                  Focus location
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    )
  } catch (error) {
    console.error('Worldview selected item modal failed', error)
    return null
  }
}

export default memo(WorldviewSelectedItemModal)
